import { useEffect, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { onAuthStateChanged } from 'firebase/auth';

import { firebaseAuthClient } from '@/src/modules/auth/auth.config';
import { IFirestoreOffer } from '@/src/modules/firestore/offers/offers.interface';
import { IFirestorePromo } from '@/src/modules/firestore/promos/promos.interface';
import { applyDiscount } from '@/src/utils/format/amount';
import {
  IDataLayerEvent,
  IDataLayerItem,
  IDataLayerUser,
} from '@/src/types/dataLayer';
import { formatDuration } from '@/src/utils/format/date';
import { formatVolume } from '@/src/utils/format/volume';
import { IFirestoreOfferPurchases } from '@/src/modules/firestore/payment/payment.interface';
import { useBraze } from '@/src/utils/braze/braze';

export const useGoogleTagManagerHandler = () => {
  const [cookieAcceptList, setCookieAcceptList] = useState();
  const pathname = usePathname();
  const { destination } = useParams();
  const brazeInstance = useBraze();

  onAuthStateChanged(firebaseAuthClient, async firebaseUser => {
    if (firebaseUser) {
      // @ts-ignore
      if (brazeInstance && cookieAcceptList?.braze === true) {
        await brazeInstance.then(braze => {
          braze?.changeUser(firebaseUser.uid);
        });
      }
    }
  });

  useEffect(() => {
    if (window) {
      window._axcb = window._axcb || [];

      window._axcb.push(function (sdk) {
        sdk.on('cookies:complete', function (choices) {
          setCookieAcceptList(choices);
        });
      });
    }
  }, []);

  const mapUserData = (): IDataLayerUser | undefined => {
    const user = firebaseAuthClient.currentUser;
    if (!user) return undefined;

    return {
      user_id: user.uid,
      user_mail: user.email,
      user_phone: user.phoneNumber,
      user_firstname: user.displayName,
    };
  };

  const mapItems = (
    offer: IFirestoreOffer,
    promo?: IFirestorePromo
  ): IDataLayerItem => {
    const discountedOffer = applyDiscount(offer, promo);

    return {
      item_id: offer.id,
      item_name: `eSIM for ${offer.zone}`,
      item_variant: formatVolume(offer.volume),
      item_category: formatDuration(offer.duration),
      item_category2: offer.zone,
      price: discountedOffer.amount / 100,
      discount: (offer.basePriceEuros - discountedOffer.amount) / 100,
      quantity: 1,
      coupon: promo?.id,
    };
  };

  const handleEventPush = (event: IDataLayerEvent) => {
    window.dataLayer?.push(event);
  };

  const getCurrentPage = (url: string) => {
    const splitPage = url.split('/');
    return splitPage[splitPage.length - 1];
  };

  // Automatically called on each pathname change
  const handleEventPreload = () => {
    if (destination === undefined && !pathname.includes('/payment/')) {
      handleEventPush({
        event: 'preload_data',
        content_group: getCurrentPage(pathname),
        user_data: mapUserData(),
      });
    }
  };

  // Called when user arrives at a product page
  const handleEventViewItem = (
    offer: IFirestoreOffer,
    promo?: IFirestorePromo
  ) => {
    handleEventPush({
      event: 'preload_data',
      content_group: getCurrentPage(pathname),
      user_data: mapUserData(),
    });

    handleEventPush({ ecommerce: null });

    handleEventPush({
      event: 'view_item',
      ecommerce: {
        value: offer.basePriceEuros / 100,
        currency: 'EUR',
        items: [mapItems(offer, promo)],
      },
    });
  };

  // Called when user arrives at payment page
  const handleEventAddToCart = (
    offer: IFirestoreOffer,
    promo?: IFirestorePromo
  ) => {
    handleEventPush({
      event: 'preload_data',
      content_group: getCurrentPage(pathname),
      user_data: mapUserData(),
    });

    handleEventPush({ ecommerce: null });

    handleEventPush({
      event: 'add_to_cart',
      ecommerce: {
        value: offer.basePriceEuros / 100,
        currency: 'EUR',
        items: [mapItems(offer, promo)],
      },
    });
  };

  // Called when payment success (before redirect)
  const handleEventPurchase = (
    offer: IFirestoreOffer,
    payment?: IFirestoreOfferPurchases,
    promo?: IFirestorePromo,
    redeemCode?: IFirestorePromo
  ) => {
    handleEventPush({
      event: 'preload_data',
      content_group: getCurrentPage(pathname),
      user_data: mapUserData(),
    });

    handleEventPush({ ecommerce: null });

    if (payment) {
      handleEventPush({
        event: 'purchase',
        user_data: mapUserData(),
        ecommerce: {
          value: offer.basePriceEuros / 100,
          currency: 'EUR',
          transaction_id: payment.id,
          coupon: redeemCode?.id,
          items: [mapItems(offer, promo)],
        },
      });
    }
  };

  useEffect(() => {
    handleEventPreload();
  }, [pathname]);

  return {
    handleEventPreload,
    handleEventViewItem,
    handleEventAddToCart,
    handleEventPurchase,
    cookieAcceptList,
    setCookieAcceptList,
  };
};
